import React, { useEffect, useState } from "react";
import { Modal, Col, Row, Accordion, Table } from "react-bootstrap";
import { adminDetailsApi } from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import { DATE_FORMAT } from "helpers/common";
import { FadeLoader } from "react-spinners";
import styled from "styled-components";

// Maps a status value to a text style
const getStatusClass = (status) => {
  switch (status) {
    case "active":
      return "text-success fw-bold";
    case "blocked":
      return "text-danger fw-bold";
    case "inactive":
      return "text-secondary fw-bold";
    default:
      return "text-dark fw-bold";
  }
};

const getStatusLabel = (status) => {
  if (!status) return "--";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

// Treat "super_admin" / "superadmin" / "super admin" as super admin, case-insensitively
const isSuperAdminRole = (role) => {
  if (!role) return false;
  return role.toLowerCase().replace(/[\s_]/g, "") === "superadmin";
};

// Generates a consistent color from a string (e.g. name) for the avatar background
const getAvatarColor = (name) => {
  const colors = [
    "#F56565", "#ED8936", "#ECC94B", "#48BB78",
    "#38B2AC", "#4299E1", "#667EEA", "#9F7AEA", "#ED64A6",
  ];
  if (!name) return "#A0AEC0";
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

// Generates initials from a name (e.g. "Vivek Kumar" -> "VK")
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  const initials =
    parts.length > 1
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`
      : parts[0][0];
  return initials.toUpperCase();
};

export default function ViewAdminModal({ show, handleClose, id }) {
  const { detailsLoading } = useSelector(({ Admin }) => Admin);
  const [data, setData] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = () => {
    dispatch(
      adminDetailsApi({
        data: { _id: id },
        callBack: (err, response) => {
          const detail = response?.data;
          if (detail?._id) {
            setData(detail);
            console.info("Role data ------->", detail);
          }
        },
      }),
    );
  };

  const roleDetails = data?.roleDetails;
  const permissions = roleDetails?.permissions;
  const isSuperAdmin = isSuperAdminRole(data?.role);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold">Admin Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {detailsLoading ? (
          <div className="d-flex justify-content-center my-5">
            <FadeLoader />
          </div>
        ) : (
          <AccordionSec className="section">
            <Accordion defaultActiveKey="0" className="w-100">

              {/* Details */}
              <Accordion.Item eventKey="0" className="border rounded mb-3">
                <Accordion.Header className="fw-bold text-dark p-0">
                  Details
                </Accordion.Header>
                <Accordion.Body>
                  <Row>
                    <Col lg={12} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Profile Pic</p>
                      {data?.profilePic ? (
                        <img
                          src={data.profilePic}
                          alt={data?.name || "Profile"}
                          style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = ""; // clear broken src
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "inline-flex";
                          }}
                        />
                      ) : null}
                      <span
                        style={{
                          display: data?.profilePic ? "none" : "inline-flex",
                          width: "48px",
                          height: "48px",
                          borderRadius: "50%",
                          backgroundColor: getAvatarColor(data?.name),
                          color: "#fff",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "16px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {getInitials(data?.name)}
                      </span>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Name</p>
                      <p>{data?.name || "--"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Email</p>
                      <p>{data?.email || "--"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Mobile Number</p>
                      <p>
                        {data?.mobileNumber
                          ? `${data?.countryCode || ""} ${data?.mobileNumber}`
                          : "--"}
                      </p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Status</p>
                      <p className={getStatusClass(data?.status)}>
                        {getStatusLabel(data?.status)}
                      </p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Role</p>
                      <p>{data?.role || "--"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Last Login At</p>
                      <p>
                        {data?.lastLoginAt ? DATE_FORMAT(data?.lastLoginAt) : "--"}
                      </p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Backup Codes</p>
                      <p>
                        {data?.twoFactorAuth?.backupCodes?.length > 0
                          ? data.twoFactorAuth.backupCodes.join(", ")
                          : "--"}
                      </p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Password Changed</p>
                      <p>{data?.isPasswordChange ? "Yes" : "No"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Last Password Change At</p>
                      <p>
                        {data?.lastPasswordChangeAt
                          ? DATE_FORMAT(data?.lastPasswordChangeAt)
                          : "--"}
                      </p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Created At</p>
                      <p>{data?.createdAt ? DATE_FORMAT(data?.createdAt) : "--"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Modified At</p>
                      <p>{data?.updatedAt ? DATE_FORMAT(data?.updatedAt) : "--"}</p>
                    </Col>

                    {/* Role Name / Description only shown for admin role, not super admin */}
                    {!isSuperAdmin && (
                      <>
                        <Col lg={6} sm={12} className="my-2">
                          <p className="mb-0 font-weight-bold">Role Name</p>
                          <p>{roleDetails?.name || "--"}</p>
                        </Col>

                        <Col lg={12} sm={12} className="my-2">
                          <p className="mb-0 font-weight-bold">Role Description</p>
                          <p>{roleDetails?.description || "--"}</p>
                        </Col>
                      </>
                    )}

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Created By (Admin Name)</p>
                      <p>{data?.adminDetails?.name || "--"}</p>
                    </Col>

                    <Col lg={6} sm={12} className="my-2">
                      <p className="mb-0 font-weight-bold">Created By (Admin Email)</p>
                      <p>{data?.adminDetails?.email || "--"}</p>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* Permissions - hidden entirely for super admin */}
              {!isSuperAdmin && (
                <Accordion.Item eventKey="1" className="border rounded mb-3">
                  <Accordion.Header className="fw-bold text-dark p-0">
                    Permissions
                  </Accordion.Header>
                  <Accordion.Body>
                    {permissions?.length > 0 ? (
                      <Table responsive bordered hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Module</th>
                            <th>Action</th>
                            <th>Permission Key</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {permissions.map((perm) => (
                            <tr key={perm._id}>
                              <td>{perm?.module || "--"}</td>
                              <td className="text-capitalize">
                                {perm?.action || "--"}
                              </td>
                              <td>{perm?.permissionKey || "--"}</td>
                              <td>{perm?.description || "--"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <p className="text-muted text-center mb-0">
                        No permissions assigned.
                      </p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              )}

            </Accordion>
          </AccordionSec>
        )}
      </Modal.Body>
    </Modal>
  );
}

const AccordionSec = styled.div`
 .accordion-header{
  .accordion-button {
   background: transparent;
  }
 }
`;